import React, { useState, useEffect } from 'react';
import './App.css';
import { Menu, X, Star, Package, Zap, Shield, ArrowRight, Truck } from 'lucide-react';
import ContactModal from './components/ContactModal';
import ReviewModal from './components/ReviewModal';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Toaster } from './components/ui/sonner';
import { brands, companyInfo, visionMission, reviews, wholesaleInfo, supplierStats } from './mock';

function App() {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <div className="App">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-sm border-b border-[#4a4a4a]">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="logo-container">
                <div className="logo-icon">
                  <Zap className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="text-2xl font-bold text-white">
                Drakify Express
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button
                onClick={() => setIsContactOpen(true)}
                className="text-white hover:text-[#a0a0a0] transition-colors font-medium"
              >
                Contact Us
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-white hover:text-[#a0a0a0] transition-colors font-medium"
              >
                About Us
              </button>
              <button
                onClick={() => setIsReviewOpen(true)}
                className="text-white hover:text-[#a0a0a0] transition-colors font-medium"
              >
                Leave a Review
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 flex flex-col gap-4">
              <button
                onClick={() => {
                  setIsContactOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="text-white hover:text-[#a0a0a0] transition-colors font-medium text-left"
              >
                Contact Us
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-white hover:text-[#a0a0a0] transition-colors font-medium text-left"
              >
                About Us
              </button>
              <button
                onClick={() => {
                  setIsReviewOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="text-white hover:text-[#a0a0a0] transition-colors font-medium text-left"
              >
                Leave a Review
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section pt-32 pb-20 px-6 min-h-screen flex items-center">
        <div className="container mx-auto">
          <div className="max-w-4xl">
            <div className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
              <p className="text-white text-sm font-semibold">🏆 Authorized B2B Wholesale Electronics Supplier</p>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Premium Wholesale Electronics Distributor
            </h1>
            <p className="text-xl md:text-2xl text-[#a0a0a0] mb-4 leading-relaxed">
              Your trusted B2B partner for 100% authentic electronics and technology products. 
              As an <span className="text-white font-semibold">authorized distributor</span>, we supply retailers, resellers, 
              e-commerce businesses, and corporate buyers across the Middle East with bulk orders at highly competitive wholesale prices.
            </p>
            <p className="text-lg md:text-xl text-[#a0a0a0] mb-6 leading-relaxed">
              We guarantee <span className="text-white font-semibold">100% genuine products from authorized distributors</span> with 
              full manufacturer warranties. Every item in our catalog is sourced directly from official channels, ensuring authenticity 
              and quality for your business.
            </p>
            
            {/* Key Benefits Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3">
                <p className="text-white font-semibold text-sm mb-1">Authorized Distributor</p>
                <p className="text-[#a0a0a0] text-xs">Official partnerships</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3">
                <p className="text-white font-semibold text-sm mb-1">Bulk Discounts</p>
                <p className="text-[#a0a0a0] text-xs">Volume pricing tiers</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3">
                <p className="text-white font-semibold text-sm mb-1">Trade Credit</p>
                <p className="text-[#a0a0a0] text-xs">Net 30/60 terms</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3">
                <p className="text-white font-semibold text-sm mb-1">Express Shipping</p>
                <p className="text-[#a0a0a0] text-xs">Fast UAE delivery</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => setIsContactOpen(true)}
                className="bg-white text-black hover:bg-[#e5e5e5] font-semibold text-lg px-8 py-6"
              >
                Request Wholesale Quote <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => scrollToSection('brands')}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-black font-semibold text-lg px-8 py-6"
              >
                View Brands
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-[#2a2a2a] border-y border-[#4a4a4a]">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {supplierStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-sm md:text-base text-[#a0a0a0]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-[#1a1a1a]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-[#2a2a2a] border-[#4a4a4a] p-8 hover:border-white transition-all">
              <Package className="w-12 h-12 text-white mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">Authorized Distributor</h3>
              <p className="text-[#a0a0a0]">
                100% genuine products from authorized distributors. Every item comes with manufacturer warranty and authenticity guarantee.
              </p>
            </Card>
            <Card className="bg-[#2a2a2a] border-[#4a4a4a] p-8 hover:border-white transition-all">
              <Zap className="w-12 h-12 text-white mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">Express Wholesale Delivery</h3>
              <p className="text-[#a0a0a0]">
                Fast and reliable bulk shipping across the UAE and Middle East region. Track your wholesale orders in real-time.
              </p>
            </Card>
            <Card className="bg-[#2a2a2a] border-[#4a4a4a] p-8 hover:border-white transition-all">
              <Shield className="w-12 h-12 text-white mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">Competitive Wholesale Pricing</h3>
              <p className="text-[#a0a0a0]">
                Best bulk pricing for retailers, resellers, and businesses. Tiered discounts based on order volume.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Wholesale Benefits Section */}
      <section className="py-20 px-6 bg-[#2a2a2a]">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {wholesaleInfo.title}
            </h2>
            <p className="text-xl text-[#a0a0a0] max-w-3xl mx-auto">
              {wholesaleInfo.description}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wholesaleInfo.benefits.map((benefit, index) => (
              <Card key={index} className="bg-[#1a1a1a] border-[#4a4a4a] p-6 hover:border-white transition-all">
                <h3 className="text-lg font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-[#a0a0a0] text-sm">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section id="brands" className="py-20 px-6">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-4">
            Premium Brands We Supply
          </h2>
          <p className="text-xl text-[#a0a0a0] text-center mb-12 max-w-2xl mx-auto">
            As an authorized wholesale distributor, we supply genuine products from leading electronics brands
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="bg-[#2a2a2a] border border-[#4a4a4a] rounded-lg p-6 flex items-center justify-center hover:border-white hover:bg-[#3a3a3a] transition-all cursor-pointer"
              >
                <span className="text-xl font-bold text-white">{brand.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 px-6 bg-[#2a2a2a]">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-12">
            What Our Clients Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review) => (
              <Card key={review.id} className="bg-[#1a1a1a] border-[#4a4a4a] p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < review.rating ? 'fill-white text-white' : 'text-[#4a4a4a]'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-[#a0a0a0] mb-4">{review.comment}</p>
                <div>
                  <p className="text-white font-semibold">{review.name}</p>
                  <p className="text-[#a0a0a0] text-sm">{review.company}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Our Vision</h2>
              <p className="text-lg text-[#a0a0a0] leading-relaxed">
                {visionMission.vision}
              </p>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Our Mission</h2>
              <p className="text-lg text-[#a0a0a0] leading-relaxed">
                {visionMission.mission}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 px-6 bg-[#2a2a2a]">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-12">
            About Drakify Express
          </h2>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xl text-[#a0a0a0] mb-6 leading-relaxed">
              Founded in {companyInfo.founded}, Drakify Express has established itself as a leading 
              B2B wholesale distributor and supplier of premium electronics and technology products in the Middle East.
            </p>
            <p className="text-xl text-[#a0a0a0] mb-6 leading-relaxed">
              We specialize in bulk orders and supply chain solutions for retailers, resellers, e-commerce businesses, 
              and corporate buyers. Our extensive network and authorized distributor partnerships enable us to offer 
              the most competitive wholesale prices in the region.
            </p>
            <p className="text-xl text-[#a0a0a0] mb-8 leading-relaxed">
              From small retailers to large-scale distributors, we serve businesses of all sizes with personalized 
              wholesale solutions, flexible payment terms, and dedicated account management.
            </p>
            <div className="bg-[#1a1a1a] border border-[#4a4a4a] rounded-lg p-6 text-left mb-8">
              <h3 className="text-xl font-bold text-white mb-4">Wholesale Inquiries</h3>
              <p className="text-[#a0a0a0] mb-2">
                <span className="font-semibold text-white">Headquarters:</span> {companyInfo.address}
              </p>
              <p className="text-[#a0a0a0] mb-2">
                <span className="font-semibold text-white">Business Email:</span> {companyInfo.email}
              </p>
              <p className="text-[#a0a0a0] mb-4">
                <span className="font-semibold text-white">Whatsapp Number:</span> {companyInfo.whatsapp}
              </p>
              <p className="text-sm text-[#a0a0a0] italic">
                For wholesale quotes and partnership inquiries, please contact our B2B sales team
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#4a4a4a]">
        <div className="container mx-auto text-center">
          <p className="text-[#a0a0a0]">
            Copyright © {companyInfo.founded} Drakify Express. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Modals */}
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
      <ReviewModal isOpen={isReviewOpen} onClose={() => setIsReviewOpen(false)} />
      <Toaster />
    </div>
  );
}

export default App;
